"use client";

import { FormEvent, useState } from "react";
import Link from "next/link";

import type { Subject, Topic } from "@/lib/api";

import { apiBaseUrl } from "@/lib/browser-api";
import { topicGenerators,generatorLink } from "@/lib/topic-generators";

type Props = { subjects: Subject[];initialGrade?:number };

export default function TopicFinder({ subjects,initialGrade }: Props) {
  const [query, setQuery] = useState("");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState(initialGrade?String(initialGrade):"");
  const [topics, setTopics] = useState<Topic[]>([]);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");

  const hasFilter = Boolean(query.trim() || subject || grade);

  async function findTopics(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!hasFilter) return;
    setState("loading");
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (subject) params.set("subject", subject);
    if (grade) params.set("grade", grade);
    try {
      const response = await fetch(`${apiBaseUrl}/api/topics?${params}`, { cache: "no-store" });
      if (!response.ok) throw new Error("topics unavailable");
      setTopics((await response.json()) as Topic[]);
      setState("ready");
    } catch {
      setState("error");
    }
  }

  function resetFinder() {
    setQuery("");
    setSubject("");
    setGrade(initialGrade?String(initialGrade):"");
    setTopics([]);
    setState("idle");
  }

  return (
    <section className="topic-finder" aria-labelledby="topic-finder-heading">
      <h2 id="topic-finder-heading">Thema direkt finden</h2>
      <p>Schreib ein Wort wie „Wiewörter“ oder wähle Fach und Klasse. Wir zeigen nur passende Themen.</p>
      <form onSubmit={findTopics}>
        <label className="finder-query">
          <span>Woran möchtest du arbeiten?</span>
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Zum Beispiel: Textaufgaben"
            type="search"
            value={query}
          />
        </label>
        <div className="finder-filters">
          <label>
            Fach
            <select aria-label="Fach" onChange={(event) => setSubject(event.target.value)} value={subject}>
              <option value="">Alle Fächer</option>
              {subjects.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label>
            Klasse
            <select aria-label="Klasse" onChange={(event) => setGrade(event.target.value)} value={grade}>
              <option value="">Alle Klassen</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => <option key={value} value={value}>Klasse {value}</option>)}
            </select>
          </label>
          <button className="button primary" disabled={!hasFilter || state === "loading"} type="submit">
            {state === "loading" ? "Suche läuft …" : "Themen finden"}
          </button>
          {hasFilter ? <button className="finder-reset" onClick={resetFinder} type="button">Zurücksetzen</button> : null}
        </div>
      </form>
      {state === "error" ? <p className="finder-message notice">Die Suche ist gerade nicht erreichbar. Bitte versuche es gleich noch einmal.</p> : null}
      {state === "ready" ? (
        <div className="finder-results" aria-live="polite">
          <p>{topics.length ? `${topics.length} passende ${topics.length === 1 ? "Thema" : "Themen"}` : "Noch kein passendes Thema gefunden."}</p>
          {topics.length ? <div className="finder-result-list">{topics.map((topic) => {
            const choices=topicGenerators(topic,grade?Number(grade):undefined);
            return <Link href={choices.length===1 ? generatorLink(choices[0]) : {pathname:`/topic/${topic.slug}`,query:grade?{grade}:{}}} key={topic.id}>
              <span>{topic.label}</span><small>{subjects.find((item) => item.id === topic.subject_id)?.label} · {choices.length ? `${choices.length} Übungstypen verfügbar` : "Noch keine Arbeitsblätter"}</small>
            </Link>
          })}</div> : null}
        </div>
      ) : null}
    </section>
  );
}
